import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card, Alert } from '../components/ui';

/**
 * ProfilePage component for viewing and editing user profile
 * @returns {React.ReactElement} ProfilePage component
 */
const ProfilePage = () => {
  const { currentUser, updateUser, hasPermission, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    phoneNumber: '',
    email: '',
    role: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Populate form data when user changes
  useEffect(() => {
    if (currentUser) {
      setFormData({
        username: currentUser.username || '',
        name: currentUser.name || '',
        phoneNumber: currentUser.phoneNumber || '',
        email: currentUser.email || '',
        role: currentUser.role || ''
      });
    }
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateUser(formData);
      setIsEditing(false);
      setSuccess('个人资料已成功更新');
    } catch (err) {
      setError(err.message || '更新个人资料时发生错误');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      setError(err.message || '登出时发生错误');
    }
  };

  // If no user is logged in
  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <Card variant="modern" padding="large" className="max-w-md w-full text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 mb-4">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
              未登录
            </h2>
            <p className="text-gray-500 mb-6">
              请先登录后再访问个人资料
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="primary"
              size="medium"
            >
              前往登录
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            个人资料
          </h1>
          <p className="text-gray-600">管理您的个人信息和账户设置</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6">
            <Alert
              type="error"
              title="错误"
              dismissible
              onDismiss={() => setError(null)}
            >
              {error}
            </Alert>
          </div>
        )}

        {/* Success Display */}
        {success && (
          <div className="mb-6">
            <Alert
              type="success"
              title="成功"
              dismissible
              onDismiss={() => setSuccess(null)}
            >
              {success}
            </Alert>
          </div>
        )}

        {/* Profile Content */}
        <Card variant="modern" padding="none" className="mb-6">
          {isEditing ? (
            /* Edit Form */
            <div>
              <div className="px-6 py-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-t-3xl">
                <h3 className="text-xl font-semibold">
                  编辑个人资料
                </h3>
                <p className="text-emerald-100 mt-1">
                  更新您的个人信息
                </p>
              </div>
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <Input
                      label="姓名"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="请输入您的姓名"
                      variant="outlined"
                    />

                    {/* Username */}
                    <Input
                      label="用户名"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="请输入用户名"
                      variant="outlined"
                    />

                    {/* Phone Number */}
                    <Input
                      label="电话号码"
                      name="phoneNumber"
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="请输入电话号码"
                      variant="outlined"
                    />

                    {/* Email */}
                    <Input
                      label="电子邮箱"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="请输入电子邮箱"
                      variant="outlined"
                    />
                  </div>

                  <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setIsEditing(false);
                        // Reset to original user data
                        if (currentUser) {
                          setFormData({
                            username: currentUser.username || '',
                            name: currentUser.name || '',
                            phoneNumber: currentUser.phoneNumber || '',
                            email: currentUser.email || '',
                            role: currentUser.role || ''
                          });
                        }
                      }}
                    >
                      取消
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={loading}
                    >
                      保存
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          ) : (
            /* View Profile */
            <div>
              <div className="px-6 py-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-t-3xl flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold">
                    个人信息
                  </h3>
                  <p className="text-emerald-100 mt-1">
                    您的账户详细信息
                  </p>
                </div>
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="secondary"
                  size="small"
                  className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                  startIcon={
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  }
                >
                  编辑资料
                </Button>
              </div>
              <div className="p-6">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Username */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <dt className="text-sm font-medium text-gray-500 mb-1">用户名</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {currentUser.username}
                    </dd>
                  </div>

                  {/* Name */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <dt className="text-sm font-medium text-gray-500 mb-1">姓名</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {currentUser.name || '未设置'}
                    </dd>
                  </div>

                  {/* Phone */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <dt className="text-sm font-medium text-gray-500 mb-1">电话号码</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {currentUser.phoneNumber || '未设置'}
                    </dd>
                  </div>

                  {/* Email */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <dt className="text-sm font-medium text-gray-500 mb-1">电子邮箱</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {currentUser.email || '未设置'}
                    </dd>
                  </div>

                  {/* Role */}
                  <div className="bg-gray-50 rounded-xl p-4 md:col-span-2">
                    <dt className="text-sm font-medium text-gray-500 mb-1">角色</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                        {currentUser.role === 'admin' ? '管理员' :
                          currentUser.role === 'manager' ? '经理' :
                            currentUser.role === 'practitioner' ? '调理师' :
                              currentUser.role === 'receptionist' ? '前台' :
                                currentUser.role}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}
        </Card>

        {/* Change Password Section */}
        <Card variant="modern" padding="medium" className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                安全设置
              </h3>
              <p className="text-gray-600">
                修改密码和安全选项
              </p>
            </div>
            <div className="ml-4">
              <svg className="h-8 w-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>

          <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
            <div>
              <h4 className="text-sm font-medium text-gray-900">修改密码</h4>
              <p className="text-sm text-gray-600 mt-1">
                定期更新密码可以提高账户安全性
              </p>
            </div>
            <Button
              onClick={() => navigate('/change-password')}
              variant="secondary"
              size="small"
            >
              修改密码
            </Button>
          </div>
        </Card>

        {/* Logout Button */}
        <div className="flex justify-center">
          <Button
            onClick={handleLogout}
            variant="danger"
            size="medium"
            startIcon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            }
          >
            退出登录
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;